import {
  RELAYABLY_COUNTRY_OPTIONS,
  RELAYABLY_DEFAULT_COUNTRY,
  RELAYABLY_NUMBER_KIND_OPTIONS,
  type RelayablyCountryCode,
  type RelayablyNumberKind,
} from '@monorepo/relayably/config'
import { Theme } from '@monorepo/relayably/theme'
import { ArrowRight, CaretDown, Check, GlobeHemisphereWest, Info, MagnifyingGlass, MapPin, WarningCircle } from 'phosphor-react-native'
import { useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native'
import { useAppSelector } from '../../../store'
import { relayablyPhoneNumberApi } from '../../relayablyPhoneNumberApi'
import { PhoneNumber } from '../types'

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'] as const
const NUMBER_TYPE_OPTIONS = RELAYABLY_NUMBER_KIND_OPTIONS

type NumberKind = RelayablyNumberKind

const capabilityOrder = ['voice', 'sms', 'mms'] as const

export const PhoneNumberStep = ({
  allowedKinds,
  onBack,
  onComplete,
  onSelectedChange,
  currentNumber,
  country: controlledCountry,
  numberKind: controlledNumberKind,
  onCountryChange,
  onNumberKindChange,
  workspaceName,
  theme,
  deferAssign = false,
}: {
  allowedKinds?: NumberKind[]
  onBack?: () => void
  onComplete?: (number: PhoneNumber) => void
  onSelectedChange?: (number: PhoneNumber | null) => void
  currentNumber?: PhoneNumber | null
  country?: RelayablyCountryCode
  numberKind?: NumberKind
  onCountryChange?: (value: RelayablyCountryCode) => void
  onNumberKindChange?: (value: NumberKind) => void
  workspaceName?: string
  theme: Theme
  deferAssign?: boolean
}) => {
  const sessionToken = useAppSelector(state => state.auth.sessionToken)
  const styles = useMemo(() => createStyles(theme), [theme])
  const [uncontrolledCountry, setUncontrolledCountry] = useState<RelayablyCountryCode>(controlledCountry ?? RELAYABLY_DEFAULT_COUNTRY)
  const [uncontrolledNumberKind, setUncontrolledNumberKind] = useState<NumberKind>(controlledNumberKind ?? 'mobile')
  const [selectedState, setSelectedState] = useState<(typeof AU_STATES)[number]>('NSW')
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [numbers, setNumbers] = useState<PhoneNumber[]>([])
  const [nextPageToken, setNextPageToken] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedNumber, setSelectedNumber] = useState<PhoneNumber | null>(currentNumber ?? null)
  const [isProvisioning, setIsProvisioning] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const country = controlledCountry ?? uncontrolledCountry
  const numberKind = controlledNumberKind ?? uncontrolledNumberKind
  const region = numberKind === 'local' ? selectedState : ''
  const showStateDropdown = numberKind === 'local' && country === 'AU'
  const permittedKinds = allowedKinds?.length ? allowedKinds : NUMBER_TYPE_OPTIONS.map(option => option.value)
  const visibleNumberTypeOptions = NUMBER_TYPE_OPTIONS.filter(option => permittedKinds.includes(option.value))
  const selectedNumberTypeOption =
    visibleNumberTypeOptions.find(option => option.value === numberKind) ?? visibleNumberTypeOptions[0] ?? NUMBER_TYPE_OPTIONS[0]

  useEffect(() => {
    if (currentNumber) {
      setSelectedNumber(currentNumber)
    }
  }, [currentNumber])

  useEffect(() => {
    if (permittedKinds.includes(numberKind)) {
      return
    }

    const fallbackKind = permittedKinds[0]
    if (!fallbackKind) {
      return
    }

    updateNumberKind(fallbackKind)
  }, [numberKind, permittedKinds])

  const updateCountry = (value: RelayablyCountryCode) => {
    if (controlledCountry === undefined) {
      setUncontrolledCountry(value)
    }

    onCountryChange?.(value)
  }

  const updateNumberKind = (value: NumberKind) => {
    if (controlledNumberKind === undefined) {
      setUncontrolledNumberKind(value)
    }

    onNumberKindChange?.(value)
  }

  useEffect(() => {
    let active = true

    const loadNumbers = async () => {
      if (!sessionToken) {
        return
      }

      setLoading(true)
      setErrorMessage(null)

      try {
        const response = await relayablyPhoneNumberApi.getAvailableNumbers(sessionToken, {
          country,
          kind: numberKind,
          region,
          query,
          pageSize: 12,
        })

        if (!active) {
          return
        }

        setNumbers(response.numbers)
        setNextPageToken(response.nextPageToken)
        setHasMore(response.hasMore)
      } catch (error) {
        if (!active) {
          return
        }

        setErrorMessage(error instanceof Error ? error.message : 'Relayably could not load phone numbers.')
        setNumbers([])
        setNextPageToken(null)
        setHasMore(false)
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    void loadNumbers()
    return () => {
      active = false
    }
  }, [country, numberKind, query, region, sessionToken])

  const handleLoadMore = async () => {
    if (!sessionToken || !nextPageToken || loadingMore) {
      return
    }

    setLoadingMore(true)
    setErrorMessage(null)

    try {
      const response = await relayablyPhoneNumberApi.getAvailableNumbers(sessionToken, {
        country,
        kind: numberKind,
        region,
        query,
        pageToken: nextPageToken,
        pageSize: 12,
      })

      setNumbers(previous => [...previous, ...response.numbers])
      setNextPageToken(response.nextPageToken)
      setHasMore(response.hasMore)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Relayably could not load more phone numbers.')
    } finally {
      setLoadingMore(false)
    }
  }

  const handleSelectNumber = async (number: PhoneNumber) => {
    setSelectedNumber(number)
    setErrorMessage(null)
    onSelectedChange?.(number)
  }

  const handleAssign = async () => {
    if (!selectedNumber || !sessionToken || !onComplete) {
      return
    }

    setIsProvisioning(true)
    setErrorMessage(null)

    try {
      const response = await relayablyPhoneNumberApi.assignNumber(sessionToken, {
        ...selectedNumber,
        friendlyName: workspaceName || selectedNumber.friendlyName,
      })

      if (response.outcome === 'pending_review') {
        setSelectedNumber(response.number)
        onSelectedChange?.(response.number)
      }

      onComplete(response.number)
    } catch (error) {
      setIsProvisioning(false)
      setErrorMessage(error instanceof Error ? error.message : 'Relayably could not assign that number.')
    }
  }

  return (
    <View style={styles.container}>
      {isProvisioning ? (
        <View style={styles.overlay}>
          <ActivityIndicator color={theme.colors.primary} size='large' />
          <Text style={styles.overlayTitle}>Provisioning Number</Text>
          <Text style={styles.overlayCopy}>Assigning {selectedNumber?.phoneNumber} to your Relayably workspace…</Text>
        </View>
      ) : null}

      {currentNumber?.status === 'active' || currentNumber?.status === 'pending_review' ? (
        <View style={styles.currentCard}>
          <View style={styles.currentCopyWrap}>
            <Text style={styles.currentEyebrow}>{currentNumber.status === 'pending_review' ? 'Number Awaiting Review' : 'Current Assigned Number'}</Text>
            <Text style={styles.currentNumber}>{currentNumber.phoneNumber}</Text>
            <Text style={styles.currentCopy}>
              {currentNumber.status === 'pending_review'
                ? 'Relayably has submitted the Twilio compliance bundle. The number will be ready once Twilio approves it.'
                : currentNumber.forwardingInstructions || 'Your inbound orchestration line is already configured.'}
            </Text>
          </View>
          <Pressable onPress={() => onComplete?.(currentNumber)} style={styles.currentAction}>
            <Text style={styles.currentActionText}>Continue With This Number</Text>
          </Pressable>
        </View>
      ) : null}

      <View style={styles.filterBlock}>
        <View style={styles.field}>
          <Text style={styles.label}>
            <GlobeHemisphereWest color={theme.colors.inactive} size={12} /> Country
          </Text>
          {RELAYABLY_COUNTRY_OPTIONS.length === 1 ? (
            <View style={styles.requirementCard}>
              <Text style={styles.requirementText}>{RELAYABLY_COUNTRY_OPTIONS[0]?.label ?? 'Australia only'}</Text>
            </View>
          ) : (
            <View style={styles.optionRow}>
              {RELAYABLY_COUNTRY_OPTIONS.map(option => (
                <Pressable
                  key={option.code}
                  onPress={() => updateCountry(option.code)}
                  style={[styles.chip, country === option.code ? styles.chipActive : undefined]}
                >
                  <Text style={[styles.chipText, country === option.code ? styles.chipTextActive : undefined]}>
                    {option.label}
                  </Text>
                </Pressable>
              ))}
            </View>
          )}
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>Number Type</Text>
          <View style={styles.optionRow}>
            {visibleNumberTypeOptions.map(option => {
              const selected = numberKind === option.value
              return (
                <Pressable
                  key={option.value}
                  onPress={() => updateNumberKind(option.value)}
                  style={[styles.chip, selected ? styles.chipActive : undefined]}
                >
                  <Text style={[styles.chipText, selected ? styles.chipTextActive : undefined]}>
                    {option.label}
                  </Text>
                </Pressable>
              )
            })}
          </View>
          <View style={styles.requirementCard}>
            <Text style={styles.requirementText}>{selectedNumberTypeOption.requirement}</Text>
          </View>
        </View>

        {showStateDropdown ? (
          <View style={styles.field}>
            <Text style={styles.label}>
              <MapPin color={theme.colors.inactive} size={12} /> State
            </Text>
            <View>
              <Pressable onPress={() => setStateDropdownOpen(open => !open)} style={styles.selectTrigger}>
                <Text style={styles.selectTriggerText}>{selectedState}</Text>
                <CaretDown color={theme.colors.inactive} size={14} />
              </Pressable>
              {stateDropdownOpen ? (
                <View style={styles.selectMenu}>
                  {AU_STATES.map(option => (
                    <Pressable
                      key={option}
                      onPress={() => {
                        setSelectedState(option)
                        setStateDropdownOpen(false)
                      }}
                      style={[styles.selectOption, selectedState === option ? styles.selectOptionActive : undefined]}
                    >
                      <Text style={[styles.selectOptionText, selectedState === option ? styles.selectOptionTextActive : undefined]}>
                        {option}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              ) : null}
            </View>
          </View>
        ) : null}

        <View style={styles.field}>
          <Text style={styles.label}>
            <MagnifyingGlass color={theme.colors.inactive} size={12} /> Search
          </Text>
          <TextInput
            onChangeText={setQuery}
            placeholder='Try numbers like 000'
            placeholderTextColor={theme.colors.inactive}
            style={styles.input}
            value={query}
          />
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Available Numbers</Text>
        <View style={styles.infoCard}>
          <Info color={theme.colors.primary} size={18} />
          <Text style={styles.infoCopy}>
            Once assigned, this becomes the Australian number you can forward calls into so Relayably can orchestrate conversations on your behalf.
          </Text>
        </View>
        {loading ? (
          <View style={styles.emptyState}>
            <ActivityIndicator color={theme.colors.primary} />
            <Text style={styles.emptyText}>Finding numbers…</Text>
          </View>
        ) : errorMessage ? (
          <View style={styles.errorCard}>
            <WarningCircle color={'#ffb86b'} size={18} weight='fill' />
            <Text style={styles.errorText}>{errorMessage}</Text>
          </View>
        ) : numbers.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No numbers matched this search. Try a different type, filter, or search term.</Text>
          </View>
        ) : (
          <View style={styles.numberSection}>
            <View style={styles.numberGrid}>
              {numbers.map(number => (
                <Pressable
                  key={number.id}
                  onPress={() => void handleSelectNumber(number)}
                  style={[styles.numberCard, selectedNumber?.id === number.id ? styles.numberCardActive : undefined]}
                >
                  <View style={styles.numberTopRow}>
                    <Text style={styles.numberText}>{number.phoneNumber}</Text>
                    {selectedNumber?.id === number.id ? <Check color={theme.colors.primary} size={18} weight='bold' /> : null}
                  </View>
                  <View style={styles.capabilityRow}>
                    {capabilityOrder
                      .filter(capability => number.capabilities.includes(capability))
                      .map(capability => (
                        <View key={capability} style={styles.capabilityTag}>
                          <Text style={styles.capabilityText}>{capability.toUpperCase()}</Text>
                        </View>
                      ))}
                  </View>
                </Pressable>
              ))}
            </View>

            {hasMore ? (
              <Pressable onPress={() => void handleLoadMore()} style={styles.loadMoreButton}>
                {loadingMore ? <ActivityIndicator color={theme.colors.primary} /> : <Text style={styles.loadMoreText}>Load more numbers</Text>}
              </Pressable>
            ) : null}
          </View>
        )}
      </View>

      {!deferAssign ? (
        <View style={styles.footerActions}>
          {onBack ? (
            <Pressable onPress={onBack} style={styles.backButton}>
              <Text style={styles.backButtonText}>Back</Text>
            </Pressable>
          ) : null}
          <Pressable
            disabled={!selectedNumber || isProvisioning}
            onPress={() => void handleAssign()}
            style={[styles.primaryButton, !selectedNumber ? styles.primaryButtonDisabled : undefined]}
          >
            <Text style={styles.primaryButtonText}>Next</Text>
            <ArrowRight color={theme.colors.staticWhite} size={16} weight='bold' />
          </Pressable>
        </View>
      ) : null}
    </View>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      gap: 24,
    },
    overlay: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      backgroundColor: 'rgba(5,6,10,0.88)',
      borderRadius: 24,
      gap: 12,
      justifyContent: 'center',
      zIndex: 20,
    },
    overlayTitle: {
      color: theme.colors.staticWhite,
      fontSize: 20,
      fontWeight: '700',
    },
    overlayCopy: {
      color: 'rgba(255,255,255,0.72)',
      fontSize: 14,
    },
    currentCard: {
      backgroundColor: theme.dark ? 'rgba(125,123,255,0.08)' : 'rgba(125,123,255,0.05)',
      borderColor: 'rgba(125,123,255,0.22)',
      borderRadius: 22,
      borderWidth: 1,
      gap: 16,
      padding: 22,
    },
    currentCopyWrap: {
      gap: 8,
    },
    currentEyebrow: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.2,
      textTransform: 'uppercase',
    },
    currentNumber: {
      color: theme.colors.text,
      fontSize: 26,
      fontWeight: '700',
    },
    currentCopy: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    currentAction: {
      alignItems: 'center',
      alignSelf: 'flex-start',
      backgroundColor: theme.colors.primary,
      borderRadius: 14,
      justifyContent: 'center',
      minHeight: 48,
      paddingHorizontal: 18,
    },
    currentActionText: {
      color: theme.colors.staticWhite,
      fontSize: 14,
      fontWeight: '700',
    },
    filterBlock: {
      gap: 18,
    },
    field: {
      gap: 10,
    },
    label: {
      alignItems: 'center',
      color: theme.colors.inactive,
      flexDirection: 'row',
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.2,
      textTransform: 'uppercase',
    },
    optionRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    chip: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f5f5fb',
      borderColor: 'rgba(125,123,255,0.12)',
      borderRadius: 999,
      borderWidth: 1,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    chipActive: {
      backgroundColor: 'rgba(125,123,255,0.12)',
      borderColor: theme.colors.primary,
    },
    chipDisabled: {
      opacity: 0.38,
    },
    chipText: {
      color: theme.colors.text,
      fontSize: 13,
      fontWeight: '600',
    },
    chipTextActive: {
      color: theme.colors.primary,
    },
    chipTextDisabled: {
      color: theme.colors.inactive,
    },
    hintText: {
      color: theme.colors.inactive,
      fontSize: 12,
      lineHeight: 18,
    },
    requirementCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
      borderColor: 'rgba(125,123,255,0.14)',
      borderRadius: 16,
      borderWidth: 1,
      paddingHorizontal: 14,
      paddingVertical: 12,
    },
    requirementTitle: {
      color: theme.colors.text,
      fontSize: 12,
      fontWeight: '700',
      marginBottom: 4,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    requirementText: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 19,
    },
    selectTrigger: {
      alignItems: 'center',
      backgroundColor: theme.dark ? '#101018' : '#f6f6fb',
      borderColor: theme.colors.border,
      borderRadius: 14,
      borderWidth: 1,
      flexDirection: 'row',
      justifyContent: 'space-between',
      minHeight: 52,
      paddingHorizontal: 16,
    },
    selectTriggerText: {
      color: theme.colors.text,
      fontSize: 15,
      fontWeight: '500',
    },
    selectMenu: {
      backgroundColor: theme.dark ? '#101018' : '#ffffff',
      borderColor: theme.colors.border,
      borderRadius: 16,
      borderWidth: 1,
      gap: 4,
      marginTop: 8,
      padding: 8,
    },
    selectOption: {
      borderRadius: 12,
      paddingHorizontal: 12,
      paddingVertical: 10,
    },
    selectOptionActive: {
      backgroundColor: 'rgba(125,123,255,0.12)',
    },
    selectOptionText: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '500',
    },
    selectOptionTextActive: {
      color: theme.colors.primary,
    },
    input: {
      backgroundColor: theme.dark ? '#101018' : '#f6f6fb',
      borderColor: theme.colors.border,
      borderRadius: 14,
      borderWidth: 1,
      color: theme.colors.text,
      fontSize: 15,
      minHeight: 52,
      paddingHorizontal: 16,
    },
    section: {
      gap: 14,
    },
    sectionTitle: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
    },
    numberSection: {
      gap: 14,
    },
    numberGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    numberCard: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
      borderColor: 'rgba(125,123,255,0.1)',
      borderRadius: 18,
      borderWidth: 1,
      flexGrow: 1,
      gap: 10,
      minWidth: Platform.OS === 'web' ? 210 : ('100%' as any),
      padding: 14,
      width: Platform.OS === 'web' ? '31%' : '100%',
    },
    numberCardActive: {
      backgroundColor: 'rgba(125,123,255,0.08)',
      borderColor: theme.colors.primary,
    },
    numberTopRow: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 10,
      justifyContent: 'space-between',
    },
    numberText: {
      color: theme.colors.text,
      flex: 1,
      fontSize: 16,
      fontWeight: '700',
    },
    capabilityRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    capabilityTag: {
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.05)' : '#ffffff',
      borderColor: theme.colors.border,
      borderRadius: 999,
      borderWidth: 1,
      paddingHorizontal: 10,
      paddingVertical: 6,
    },
    capabilityText: {
      color: theme.colors.inactive,
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 0.8,
    },
    loadMoreButton: {
      alignItems: 'center',
      alignSelf: 'center',
      borderColor: theme.colors.primary,
      borderRadius: 14,
      borderWidth: 1,
      justifyContent: 'center',
      minHeight: 48,
      minWidth: 160,
      paddingHorizontal: 18,
    },
    loadMoreText: {
      color: theme.colors.primary,
      fontSize: 14,
      fontWeight: '700',
    },
    emptyState: {
      alignItems: 'center',
      backgroundColor: theme.dark ? 'rgba(255,255,255,0.03)' : '#f7f7fb',
      borderColor: theme.colors.border,
      borderRadius: 20,
      borderWidth: 1,
      gap: 12,
      padding: 28,
    },
    emptyText: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
      textAlign: 'center',
    },
    errorCard: {
      alignItems: 'center',
      backgroundColor: 'rgba(255,184,107,0.08)',
      borderColor: 'rgba(255,184,107,0.25)',
      borderRadius: 20,
      borderWidth: 1,
      flexDirection: 'row',
      gap: 12,
      padding: 18,
    },
    errorText: {
      color: theme.colors.text,
      flex: 1,
      fontSize: 14,
      lineHeight: 20,
    },
    infoCard: {
      alignItems: 'flex-start',
      backgroundColor: theme.dark ? 'rgba(125,123,255,0.06)' : 'rgba(125,123,255,0.05)',
      borderColor: 'rgba(125,123,255,0.18)',
      borderRadius: 20,
      borderWidth: 1,
      flexDirection: 'row',
      gap: 12,
      padding: 18,
    },
    infoCopy: {
      color: theme.colors.inactive,
      flex: 1,
      fontSize: 14,
      lineHeight: 22,
    },
    footerActions: {
      flexDirection: 'row',
      gap: 12,
      justifyContent: 'space-between',
    },
    backButton: {
      alignItems: 'center',
      borderColor: theme.colors.border,
      borderRadius: 16,
      borderWidth: 1,
      justifyContent: 'center',
      minHeight: 54,
      paddingHorizontal: 22,
    },
    backButtonText: {
      color: theme.colors.text,
      fontSize: 15,
      fontWeight: '700',
    },
    primaryButton: {
      alignItems: 'center',
      backgroundColor: theme.colors.primary,
      borderRadius: 16,
      flex: 1,
      flexDirection: 'row',
      gap: 8,
      justifyContent: 'center',
      minHeight: 54,
      paddingHorizontal: 24,
    },
    primaryButtonDisabled: {
      opacity: 0.45,
    },
    primaryButtonText: {
      color: theme.colors.staticWhite,
      fontSize: 15,
      fontWeight: '700',
    },
  })
